import {
  AUDIENCES,
  AUDIENCE_LABELS,
  FAMILY_LABELS,
  GLASSES_TYPES,
  GLASSES_TYPE_LABELS,
  PRODUCT_FAMILIES,
  type Audience,
  type FrameShape,
  type GlassesType,
  type ProductFamily,
} from "../../catalog/taxonomy";
import { FrameShapeSelect } from "./FrameShapeSelect";

/** What the administrator has chosen so far. */
export interface Classification {
  family: ProductFamily | null;
  audience: Audience | null;
  glassesType: GlassesType | null;
  frameShape: FrameShape | null;
}

export const EMPTY_CLASSIFICATION: Classification = {
  family: null,
  audience: null,
  glassesType: null,
  frameShape: null,
};

/**
 * Is this classification complete enough to move on?
 *
 * Glasses need all three decisions. The other two families need none of them —
 * they are not glasses, and asking a contact-lens product for a frame shape
 * would be asking for an answer that cannot exist.
 */
export function isClassificationComplete(value: Classification): boolean {
  if (value.family === null) return false;
  if (value.family !== "glasses") return true;
  return (
    value.audience !== null && value.glassesType !== null && value.frameShape !== null
  );
}

/**
 * One answer from a fixed set, as native radios sharing a name.
 *
 * Exported because the metadata editor asks the same questions of a product
 * that already exists. A second copy of this control is how the create screen
 * and the edit screen drift into offering different answers to "Who are they
 * for?" — so there is one, and both use it.
 */
export function Choice<T extends string>({
  legend,
  hint,
  name,
  options,
  labels,
  value,
  onChange,
}: {
  legend: string;
  hint?: string;
  name: string;
  options: readonly T[];
  labels: Record<T, string>;
  value: T | null;
  onChange: (next: T) => void;
}) {
  return (
    <fieldset className="field">
      <legend className="field__label">{legend}</legend>
      {hint ? <p className="field__hint">{hint}</p> : null}
      <div className="choiceRow">
        {options.map((option) => (
          <label
            key={option}
            className={value === option ? "choice is-selected" : "choice"}
          >
            <input
              type="radio"
              name={name}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
            />
            <span className="choice__label">{labels[option]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The first step of adding a product: what kind of thing is it?
 *
 * Family comes first and everything else follows from it, because the answer
 * changes which questions are even meaningful. The three families are shown as
 * three families — not flattened into one list next to Men and Women, which
 * would put a customer segment and a product kind on the same footing.
 *
 * The glasses questions appear only once Glasses is chosen, so an administrator
 * adding an accessory is never shown a frame shape to leave blank — and an
 * accessory is never asked one either, which is why choosing either of the
 * other two families simply moves on. All three are operational: the two
 * non-glasses families are described by ordinary commerce fields and their own
 * photographs, which the step below this one asks for.
 */
export function ClassificationStep({
  value,
  onChange,
}: {
  value: Classification;
  onChange: (next: Classification) => void;
}) {
  const setFamily = (family: ProductFamily) => {
    // Switching family clears the glasses-only answers rather than carrying a
    // stale frame shape onto a product that cannot have one.
    if (family === value.family) return;
    onChange({ ...EMPTY_CLASSIFICATION, family });
  };

  return (
    <section className="step" aria-label="Product type">
      <Choice
        legend="What kind of product is this?"
        name="product-family"
        options={PRODUCT_FAMILIES}
        labels={FAMILY_LABELS}
        value={value.family}
        onChange={setFamily}
      />

      {value.family === "glasses" ? (
        <>
          <Choice
            legend="Who are they for?"
            name="audience"
            options={AUDIENCES}
            labels={AUDIENCE_LABELS}
            value={value.audience}
            onChange={(audience) => onChange({ ...value, audience })}
          />
          <Choice
            legend="Sunglasses or eyeglasses?"
            name="glasses-type"
            options={GLASSES_TYPES}
            labels={GLASSES_TYPE_LABELS}
            value={value.glassesType}
            onChange={(glassesType) => onChange({ ...value, glassesType })}
          />
          <FrameShapeSelect
            value={value.frameShape}
            onChange={(frameShape) => onChange({ ...value, frameShape })}
          />
        </>
      ) : null}

    </section>
  );
}
